import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useFeature } from '@/shared/features/useFeature';
import { radarApi } from '../services/radarApi';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

/**
 * useRadarCount — quantas recomendações estão em "novo" (ainda não trabalhadas).
 *
 * Alimenta o badge ao lado de "Radar de Empresas" na sidebar (mesmo render do
 * badge da caixa de suporte). Só faz poll com a feature `radar` ligada, então
 * tenant sem Radar nunca paga a requisição.
 *
 * ⚠️ **Não é "da semana", e a versão anterior dizia que era.** Ela mandava
 * `novidades: true` na query — um parâmetro que **nem o BFF repassava nem o
 * sales-api conhecia**, então ele nunca recortou nada e o badge sempre contou
 * todas as "novo" do tenant. Um filtro que não filtra é pior que filtro nenhum:
 * a contagem estava certa por acidente e a docstring, errada.
 *
 * Contar as "novo" é o comportamento CERTO para um badge — ele existe para
 * dizer "há trabalho a fazer", não "houve entrega nesta semana".
 */
export function useRadarCount(): { count: number } {
  const { isAuthenticated, user } = useAuth();
  const { enabled: radarEnabled } = useFeature('radar');

  const { data } = useQuery({
    queryKey: ['radar', 'count', user?.tenantId],
    queryFn: () => radarApi.list({ status: 'novo', limit: 1, offset: 0 }),
    enabled: isAuthenticated && radarEnabled,
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    // Never block render: errors silence the badge (count stays 0).
    retry: false,
    staleTime: 60_000,
  });

  return { count: data?.total ?? 0 };
}
